import type {RegionBucket} from "./regionService";

export type CookieConsent = {
  version: number;
  analytics: boolean;
  marketing: boolean;
  updatedAt: number;
  acknowledgedAt?: number;
};

const CONSENT_VERSION = 1;
const CONSENT_STORAGE_KEY = "kfa_cookie_consent";
const COOKIE_SETTINGS_OPEN_KEY = "kfa_cookie_settings_open";
export const COOKIE_SETTINGS_EVENT = "kfa_cookie_settings_open";

function readConsent(): CookieConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsent;
    if (!parsed?.version) return null;
    return parsed;
  } catch (error) {
    console.warn("Failed to read cookie consent:", error);
    return null;
  }
}

function writeConsent(consent: CookieConsent): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
  } catch (error) {
    console.warn("Failed to store cookie consent:", error);
  }
}

export function getStoredConsent(): CookieConsent | null {
  return readConsent();
}

export function setStoredConsent(consent: CookieConsent): void {
  writeConsent(consent);
}

export function buildConsent(
  analytics: boolean,
  marketing: boolean,
  acknowledge = true,
): CookieConsent {
  const now = Date.now();
  return {
    version: CONSENT_VERSION,
    analytics,
    marketing,
    updatedAt: now,
    ...(acknowledge ? {acknowledgedAt: now} : {}),
  };
}

export function getDefaultConsent(regionBucket: RegionBucket): Pick<CookieConsent, "analytics" | "marketing"> {
  if (regionBucket === "EU" || regionBucket === "UK" || regionBucket === "UNKNOWN") {
    return {analytics: false, marketing: false};
  }
  return {analytics: true, marketing: true};
}

export function isAnalyticsAllowed(
  regionBucket?: RegionBucket,
  consent?: CookieConsent | null,
): boolean {
  if (!regionBucket) return false;
  if (regionBucket === "EU" || regionBucket === "UK" || regionBucket === "UNKNOWN") {
    return consent?.analytics === true;
  }
  return consent?.analytics ?? true;
}

export function isMarketingAllowed(
  regionBucket?: RegionBucket,
  consent?: CookieConsent | null,
): boolean {
  if (!regionBucket) return false;
  if (regionBucket === "EU" || regionBucket === "UK" || regionBucket === "UNKNOWN") {
    return consent?.marketing === true;
  }
  return consent?.marketing ?? true;
}

export function shouldShowConsentBanner(
  regionBucket?: RegionBucket,
  consent?: CookieConsent | null,
): boolean {
  if (!regionBucket) return false;
  if (regionBucket === "EU" || regionBucket === "UK" || regionBucket === "UNKNOWN") {
    return !consent;
  }
  if (regionBucket === "US_OPT_OUT") {
    return !consent?.acknowledgedAt;
  }
  return false;
}

export function openCookieSettings(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(COOKIE_SETTINGS_OPEN_KEY, "true");
    window.dispatchEvent(new Event(COOKIE_SETTINGS_EVENT));
  } catch (error) {
    console.warn("Failed to trigger cookie settings:", error);
  }
}

export function consumeCookieSettingsOpenFlag(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const value = window.localStorage.getItem(COOKIE_SETTINGS_OPEN_KEY);
    if (value === "true") {
      window.localStorage.removeItem(COOKIE_SETTINGS_OPEN_KEY);
      return true;
    }
    return false;
  } catch (error) {
    console.warn("Failed to read cookie settings flag:", error);
    return false;
  }
}
